import { useFormikContext } from 'formik-2';
import PropTypes from 'prop-types';
import React from 'react';
import { Translate } from 'react-localize-redux';
import { WarningMessage } from '../../components/widgets';
import { ADMIN_EDITABLE_STATUSES, STATUSES, TRANSLATE_PREFIX } from '../consts';
import TabGroupedErrors from './TabGroupedErrors';

const FormActionWarnings = ({
    error,
    isBusy,
    isSaveDraftDisabled,
    readOnly,
    existingRequest,
    isLastTab,
    computedTabs,
    draftBlockedReasons,
}) => {
    const { dirty, isValid, errors } = useFormikContext();
    const isAdminEditing = !!existingRequest && !readOnly && ADMIN_EDITABLE_STATUSES.includes(existingRequest.status);

    const showDraftBlocked =
        !isBusy && isSaveDraftDisabled && !readOnly && (!existingRequest || existingRequest.status === STATUSES.DRAFT);

    const showSaveBlocked = isLastTab && !isBusy && !readOnly && isAdminEditing && (!dirty || !isValid);

    const showSubmitBlocked = isLastTab && !isBusy && !isValid && !readOnly && !isAdminEditing;

    if (!error && !showDraftBlocked && !showSaveBlocked && !showSubmitBlocked) {
        return null;
    }

    return (
        <>
            {error && <WarningMessage type="error" message={<Translate id={error} />} />}
            {showDraftBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.DRAFT_BLOCKED_TITLE`}
                    message={
                        draftBlockedReasons.length > 0 ? (
                            <TabGroupedErrors
                                tabs={computedTabs}
                                getTabErrors={tab =>
                                    draftBlockedReasons
                                        .filter(r => r.tabId === tab.id)
                                        .map(r => ({ key: r.reasonKey, messageKey: r.reasonKey }))
                                }
                            />
                        ) : (
                            <Translate id={`${TRANSLATE_PREFIX}.ERRORS.NO_CHANGES`} />
                        )
                    }
                />
            )}
            {showSaveBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.SAVE_BLOCKED_TITLE`}
                    message={
                        !isValid ? (
                            <TabGroupedErrors
                                tabs={computedTabs}
                                getTabErrors={tab =>
                                    Object.entries(errors)
                                        .filter(([key]) => key.startsWith(`formData.${tab.id}.`))
                                        .map(([key, errKey]) => ({ key, messageKey: errKey }))
                                }
                            />
                        ) : (
                            <Translate id={`${TRANSLATE_PREFIX}.ERRORS.NO_CHANGES`} />
                        )
                    }
                />
            )}
            {showSubmitBlocked && (
                <WarningMessage
                    type="warning"
                    showIcon={false}
                    collapsible
                    collapsibleTitle={`${TRANSLATE_PREFIX}.ERRORS.SUBMIT_BLOCKED_TITLE`}
                    message={
                        <TabGroupedErrors
                            tabs={computedTabs}
                            getTabErrors={tab =>
                                Object.entries(errors)
                                    .filter(([key]) => key.startsWith(`formData.${tab.id}.`))
                                    .map(([key, errKey]) => ({ key, messageKey: errKey }))
                            }
                        />
                    }
                />
            )}
        </>
    );
};

FormActionWarnings.propTypes = {
    error: PropTypes.string,
    isBusy: PropTypes.bool.isRequired,
    isSaveDraftDisabled: PropTypes.bool.isRequired,
    readOnly: PropTypes.bool.isRequired,
    existingRequest: PropTypes.shape({
        status: PropTypes.string,
    }),
    isLastTab: PropTypes.bool.isRequired,
    computedTabs: PropTypes.arrayOf(PropTypes.shape({ id: PropTypes.string.isRequired })),
    draftBlockedReasons: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
};

export default FormActionWarnings;
